import { NumberInputProps as MantineNumberInputProps } from "@mantine/core";
import { NumberInput as MantineNumberInput } from "@mantine/core";
import cx from "clsx";
import classes from "./index.module.css";
import { useState } from "react";
import { getInputProps } from "@/components/inputProps";

interface NumberInputProps extends MantineNumberInputProps {
  isSectionDropdown?: boolean;
  /** Marks the rendered input as containing personal data so analytics tools exclude it (adds the `piiData` class). */
  pii?: boolean;
}

export const NumberInput = ({ pii = false, isSectionDropdown, ...props }: NumberInputProps) => {
  const [focused, setFocused] = useState(false);
  const allowedDecimalSeparators = props.allowedDecimalSeparators ?? [".", ","];
  const decimalSeparator = props.decimalSeparator ?? ".";

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    props.onPaste?.(event);
    if (event.defaultPrevented) {
      return;
    }
    const pastedText = event.clipboardData.getData("text");
    const normalizedText = allowedDecimalSeparators.reduce(
      (text, separator) => (separator === decimalSeparator ? text : text.split(separator).join(decimalSeparator)),
      pastedText
    );
    if (normalizedText === pastedText) {
      return;
    }
    event.preventDefault();
    const input = event.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const nextValue = input.value.slice(0, start) + normalizedText + input.value.slice(end);

    // Bypass React's value tracker so the native "input" event below is picked up as a real change.
    Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set?.call(input, nextValue);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  };

  return (
    <MantineNumberInput
      allowedDecimalSeparators={[".", ","]}
      hideControls
      allowDecimal={false}
      allowLeadingZeros={false}
      allowNegative={false}
      {...props}
      {...getInputProps(props, focused)}
      className={cx(props.className, { piiData: pii })}
      classNames={{
        ...classes,
        ...props.classNames,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onPaste={handlePaste}
      wrapperProps={{
        "data-focused": focused,
        "data-left-section-dropdown": isSectionDropdown,
      }}
      size="lg"
    />
  );
};
